/**
 * Builds the player's score list at start-up: one entry per (unit game,
 * game type) the data turns on, with saved progress copied in from
 * localStorage.
 *
 * Pure functions (no config, no localStorage) so they can be tested; the
 * caller in src/index.js supplies both.
 */

const emptyScores = (easyDifficulty) => {
  const difs = {
    easy: easyDifficulty,
    medium: easyDifficulty + 1,
    hard: easyDifficulty + 2,
  };
  const difficultyScores = {};
  Object.keys(difs).forEach((key) => {
    difficultyScores[key] = {
      difficulty: difs[key],
      correct: 0,
      incorrect: 0,
      score: 0,
      time: 0,
      stars: 0,
      firstStars: 0,
      secondStars: 0,
      firstNextGoals: null,
      secondNextGoals: null,
    };
  });
  return difficultyScores;
};


/**
 * A fresh (no progress) score list from the current data.
 *
 * @param {Array} data - KZ_GameFilterDataNew rows (one per unit game)
 * @param {Array} goldStar - KZ_GoldStar rows (scoring rules per game)
 * @param {Array<string>} possibleGames - config.PossibleGames
 * @returns {{scoreData: Array, totalStars: number}}
 */
export const buildScoreData = (data, goldStar, possibleGames) => {
  let totalStars = 0;
  const scoreData = [];
  data
    .filter((d) => isNaN(d.Key.length))
    .forEach((game) => {
      possibleGames.forEach((gameType) => {
        const shortGameType = gameType.replaceAll(" ", "").replaceAll("-", "");
        const goldStarName = shortGameType;
        const scoreType = goldStar.find((type) => type.game === goldStarName);
        // A game with no GoldStar row has no scoring rules, so it isn't
        // offered. Verb Activity and From Me To You used to be let through
        // anyway, which crashed the whole app (and again inside
        // FullGameContainer) if their row was ever missing.
        if (scoreType === undefined) {
          return;
        }
        const easyDifficulty = game[gameType];
        totalStars += scoreType.secondType === "none" ? 9 : 18;
        if (easyDifficulty !== null) {
          scoreData.push({
            difficultyScores: emptyScores(easyDifficulty),
            Key: `${game.Key}${gameType}`,
            scoreType: scoreType.scoreType,
            category: game.category,
            gameType: gameType,
            target: game.target,
            firstType: scoreType.type,
            secondType: scoreType.secondType,
          });
        }
      });
    });
  return { scoreData, totalStars };
};

/**
 * Copies saved progress onto the fresh list, matched by Key.
 *
 * Saved progress used to replace the fresh list outright, so a game added to
 * the data later (Solve It, for one) never reached a player who had already
 * played. Now every current game is listed, with its saved scores if it has
 * any. A saved game the current data doesn't have is kept rather than dropped,
 * so progress is never lost to a removed game or a half-loaded sheet.
 *
 * @param {Array} fresh - From buildScoreData
 * @param {Array} saved - The saved list (localStorage)
 * @returns {Array}
 */
export const mergeSavedScores = (fresh, saved) => {
  const savedByKey = new Map(saved.map((d) => [d.Key, d]));
  const merged = fresh.map((game) => {
    const savedGame = savedByKey.get(game.Key);
    return savedGame?.difficultyScores
      ? { ...game, difficultyScores: savedGame.difficultyScores }
      : game;
  });
  const freshKeys = new Set(fresh.map((d) => d.Key));
  saved.forEach((d) => {
    if (!freshKeys.has(d.Key)) merged.push(d);
  });
  return merged;
};

/**
 * Stars earned and available across a score list (for the player level).
 *
 * @param {Array} scoreData
 * @returns {{currentStars: number, totalStars: number}}
 */
export const countStars = (scoreData) => {
  let currentStars = 0;
  let totalStars = 0;
  scoreData.forEach((game) => {
    const scores = game.difficultyScores;
    if (scores !== undefined) {
      currentStars += scores.easy.stars + scores.medium.stars + scores.hard.stars;
    }
    totalStars += game.secondType === "none" ? 9 : 18;
  });
  return { currentStars, totalStars };
};
